import { FormLayout, TextInput } from '@kcftech/design-system';
import { RegisterOptions, UseFormReturn, useFormState } from 'react-hook-form';

export type AssemblyApplicationFormData = Readonly<{
    name: string;
    code: string;
}>;

const maxAssemblyApplicationNameLength = 50;
const maxAssemblyApplicationCodeLength = 16; // maybe update to 32?

const getNameRules = (
    siblingNames: string[],
): RegisterOptions<AssemblyApplicationFormData, 'name'> => ({
    required: 'Please enter the assembly application name',
    maxLength: {
        value: maxAssemblyApplicationNameLength,
        message: `Assembly application name cannot be greater than ${maxAssemblyApplicationNameLength} characters`,
    },
    validate: (value) => {
        if (value.trim() === '') {
            return 'Please enter the assembly application name';
        }

        if (siblingNames.includes(value.trim())) {
            return 'Name already exists. Please enter a unique name.';
        }
        return undefined;
    },
});

const getCodeRules = (
    siblingCodes: string[],
): RegisterOptions<AssemblyApplicationFormData, 'code'> => ({
    required: 'Please enter the assembly application code',
    maxLength: {
        value: maxAssemblyApplicationCodeLength,
        message: `Assembly application code cannot be greater than ${maxAssemblyApplicationCodeLength} characters`,
    },
    validate: (value) => {
        if (value.trim() === '') {
            return 'Please enter the assembly application code';
        }

        if (siblingCodes.includes(value.trim())) {
            return 'Code already exists. Please enter a unique code.';
        }
        return undefined;
    },
});

export type AssemblyApplicationFormContentProps = Readonly<{
    form: UseFormReturn<AssemblyApplicationFormData>;
    isEdit?: boolean;
    siblingNames: string[];
    siblingCodes: string[];
}>;

export function AssemblyApplicationFormContent({
    form,
    isEdit,
    siblingNames,
    siblingCodes,
}: AssemblyApplicationFormContentProps) {
    const {
        register,
        control,
    } = form;

    const {
        errors,
    } = useFormState({
        control,
    });

    return (
        <FormLayout>
            <TextInput
                label="Name"
                error={errors.name?.message}
                required
                placeholder="Enter a name for the assembly application"
                {...register('name', getNameRules(siblingNames))}
            />
            {!isEdit && (
                <TextInput
                    label="Code"
                    error={errors.code?.message}
                    required
                    placeholder="Enter a code for the assembly application"
                    {...register('code', getCodeRules(siblingCodes))}
                />
            )}
        </FormLayout>
    );
}
